import { PauseRounded, PlayArrowRounded } from '@mui/icons-material';
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import { Box, CircularProgress, LinearProgress, Slider, Stack, SvgIcon, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import { useAtom, useAtomValue } from 'jotai';
import { debounce } from 'lodash';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { artifactAtom } from '../../../stores/jotai/artifacts/artifacts';
import { baseAtom, getAssemblyTypeFromInterview, setCurrentTranscriptionForcePlayFlag, setPlaybackRate } from '../../../stores/jotai/base';
import { interviewAtom } from '../../../stores/jotai/interviews/currentInterview';
import { appLog } from '../../../util';
import toHHMMSS from '../../../util/toHHMMSS';
import useInterval from '../../../util/use_interval';
import IwIconButton from '../../app/IwIconButton';
import { discoverySessionAtom } from '../../../stores/jotai/discovery-session';
import PlayAudioIcon from '../../../../icons/PlayAudioIcon';
import PauseAudioIcon from '../../../../icons/PauseAudioIcon';

const PLAYBACK_RATE_CHANGE = 0.1;

export default function MaterialTranscriptionAudioPlayer(props) {
    const [vultrKey, setVultrKey] = useState(undefined);
    const [lastVultrKey, setLastVultrKey] = useState(undefined);
    const [reflectRealTimeMediaUpdate, setReflectRealtimeMediaUpdate] = useState(false);
    const [base, setBase] = useAtom(baseAtom);
    const artifact = useAtomValue(artifactAtom);
    const [interviewState,] = useAtom(interviewAtom);
    const [currentSkipMax, setCurrentSkipMax] = useState(1);

    let sliderRef = useRef(null);

    const [isSeeking, setIsSeeking] = useState(false);
    const [play, setPlay] = useState(false);
    const [waiting, setWaiting] = useState(true);
    const [seek, setSeek] = useState(0);
    const currentRate = base.playbackRate || 1.0;
    const playerStatus = window?.bplayer?.isReady();
    const mediaArtifactLength = base?.encodedMediaArtifact?.length;
    const teamID = base?.team?.groupID;
    const { listenWait, assemblies } = props;
    const theme = useTheme();
    const discoverySession = useAtomValue(discoverySessionAtom);

    const currentAssembly = assemblies?.find(assembly => assembly?.reservation === base.selectedInterview);

    const __pause = () => {
        setPlay(false);
        
        try {
            window?.bplayer.pause();
        }
        catch (e) {

        }
    }

    React.useEffect(() => {
        setCurrentSkipMax(interviewState?.speakerDictionary?.length);
    }, [interviewState]);

    const __play = () => {
        setPlay(true);
        try {
            window?.bplayer?.play();
            window?.bplayer?.play();
        }
        catch (e) {

        }
    }

    const togglePlay = () => {
        if (play) {
            __pause();
        } else {
            __play();
        }
    }

    const increasePlaybackRate = () => {
        if (currentRate < 2) {
            setPlaybackRate(
                setBase,
                parseFloat((currentRate + PLAYBACK_RATE_CHANGE).toFixed(1))
            );
        }
    };

    const decreasePlaybackRate = () => {
        if (currentRate > 0.1) {
            setPlaybackRate(
                setBase,
                parseFloat((currentRate - PLAYBACK_RATE_CHANGE).toFixed(1))
            );
        }
    };

    useEffect(() => {
        try {
            //@ts-ignore
            window?.bplayer?.setPlaybackRate(base.playbackRate);
        }
        catch (e) {

        }
    }, []);

    useEffect(() => {
        try {
            //@ts-ignore
            window?.bplayer?.setPlaybackRate(base.playbackRate);
        }
        catch (e) {

        }
    }, [base.playbackRate]);

    React.useEffect(() => {
        if (base.transcriptPlayNextFrame !== false) {


            __play();


            setCurrentTranscriptionForcePlayFlag(setBase, false);
        }
    }, [base.transcriptPlayNextFrame])

    React.useEffect(() => {
        return () => {
            window?.bplayer.pause();
        }
    }, []);

    React.useEffect(() => {
        if (vultrKey !== undefined) {
            if (lastVultrKey !== vultrKey || reflectRealTimeMediaUpdate) {
                setLastVultrKey(vultrKey);
                setReflectRealtimeMediaUpdate(false);

                if (listenWait) {
                    listenWait(false);
                }

                try {
                    let mediaSource = `${process.env.NEXT_PUBLIC_LINX_URL}/storage/mark/access/s3?artifactID=${vultrKey}&teamID=${teamID}&userID=${base?.user?.userID}&sessuinID=${discoverySession?.sessionID}`;
                    appLog(`audio source: ${mediaSource}`);
                    
                    //@ts-ignore
                    window?.bplayer?.reset();
                    //@ts-ignore
                    window?.bplayer?.initialize(document.querySelector("#dashjswindowplayer"), mediaSource, false);

                }
                catch (e) {

                }
            }
        }
    }, [vultrKey, lastVultrKey, reflectRealTimeMediaUpdate, teamID, listenWait]);

    React.useEffect(() => {
        try {
            setVultrKey(artifact?.media?.[base?.selectedInterview]?.artifactID);
        } catch (e) { }
    }, [base.selectedInterview, artifact.media, assemblies]);

    React.useEffect(() => {
        setWaiting(!playerStatus);
    }, [playerStatus]);

    React.useEffect(() => {
        if (mediaArtifactLength > 0) {
            setReflectRealtimeMediaUpdate(true);
        }
    }, [mediaArtifactLength, currentAssembly]);

    useInterval(
        () => {
            try {
                setPlay(!window?.bplayer?.isPaused());
            } catch (e) { }
        },
        100
    )

    useEffect(() => {
        try {
            //@ts-ignore
            window.canSeeTranscript = true;
        }
        catch (e) {

        }
        return () => {
            try {
                //@ts-ignore
                window.canSeeTranscript = false;
            }
            catch (e) {

            }
        };
    });

    useHotkeys(
        ["space"],
        (event: KeyboardEvent) => {
            event.preventDefault();
            togglePlay();
        }
    );

    useHotkeys(
        ["ctrl+space"],
        (event: KeyboardEvent) => {
            event.preventDefault();
            togglePlay();
        },
        { enableOnFormTags: true, enableOnContentEditable: true }
    );

    useHotkeys(
        ["shift+comma"],
        (event) => {
            event.preventDefault();
            decreasePlaybackRate();
        }
    );

    useHotkeys(
        ["ctrl+shift+comma"],
        (event) => {
            event.preventDefault();
            decreasePlaybackRate();
        },
        { enableOnFormTags: true, enableOnContentEditable: true }
    );

    useHotkeys(
        ["shift+period"],
        (event) => {
            event.preventDefault();
            increasePlaybackRate();
        }
    );

    useHotkeys(
        ["ctrl+shift+period"],
        (event) => {
            event.preventDefault();
            increasePlaybackRate();
        },
        { enableOnFormTags: true, enableOnContentEditable: true }
    );

    if (vultrKey === undefined) {
        return <LinearProgress />
    }

    if (waiting) {

        let type = "audio";

        const interm = getAssemblyTypeFromInterview(assemblies, base?.selectedInterview);

        if (interm !== undefined) {
            type = interm;
        }

        return <Stack sx={{
            borderRadius: '4px', border: '1px solid #DCDCDC',
            background: 'white', padding: '10px', height: '130px', paddingTop: '25px'
        }} alignItems="center"><CircularProgress /><Typography>Please wait while we analyze your {type} for playback...</Typography><Box sx={{ height: '0px' }}></Box></Stack>
    }

    return <Stack className="dirty-insight-editor-clickblocker-effect--deactivate" sx={{
        borderBottom: "1px solid #c2c2c2",
        background: 'white', padding: '7px 12px'
    }}>

        <Stack direction="row" sx={{alignItems:'center',justifyContent:'center',gap:'10px'}}>
            
            <Stack
                direction="row"
                sx={{
                    padding: "6px",
                    border: `1px solid ${theme.custom.borderGrey}`,
                    borderRadius: "4px",
                    justifyContent: "space-arround",
                    alignItems: "center",
                    gap: "2px",
               }}
            >
                <IconButton
                    size="small"
                    sx={{ padding: 0 }}
                    disabled={currentRate <= 0.1}
                    onClick={decreasePlaybackRate}
                >
                    <RemoveOutlinedIcon />
                </IconButton>
                <Typography>
                    {currentRate.toFixed(1)}x
                </Typography>
                <IconButton
                    size="small"
                    sx={{ padding: 0 }}
                    disabled={currentRate >= 2}
                    onClick={increasePlaybackRate}
                >
                    <AddOutlinedIcon />
                </IconButton>
            </Stack>

            <Stack 
            direction="row"
            sx={{
                marginRight: "2px",
                position: "relative",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
                <IwIconButton
                aria-label={play ? 'pause' : 'play'}
                //showBorder
                onClick={play ? __pause : __play}
                sx={{p:0}}
                >
                {play
                    ? <PauseAudioIcon width={35} height={35}/>
                    : <PlayAudioIcon width={35} height={35} />
                }
                </IwIconButton>
            </Stack>
            </Stack>
            <Slider
                aria-label="transcription-time-indicator"
                size="large"
                id="iw-transcript-slider"
                min={0}
                step={0.01} // the duration 
                ref={sliderRef}
                max={props.duration}
                value={isSeeking ? seek : props.audioTime}
                onMouseDown={() => {
                    //setPlay(false);
                    setIsSeeking(true);
                }}
                onMouseUp={() => {
                    //audioRef.current.seek(displaySeek);
                    //__seek(displaySeek);
                    setIsSeeking(false);

                    try {
                        //@ts-ignore
                        window?.bplayer?.seek(seek);
                    } catch (e) { }
                }}
                onChange={(_, value) => {
                    setSeek(value as number);
                }}
                sx={{
                    height: 8,
                    '& .MuiSlider-thumb': {
                        width: 14,
                        height: 14,
                        transition: '0.3s cubic-bezier(.47,1.64,.41,.8)',
                        '&:before': {
                            boxShadow: '0 2px 12px 0 rgba(0,0,0,0.4)',
                        },
                        '&.Mui-active': {
                            width: 14,
                            height: 14,
                        },
                    },
                    '& .MuiSlider-rail': {
                        opacity: 0.28,
                    },
                }}
            />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    // mt: -1,
                }}
            >
                <Typography>{toHHMMSS(props.audioTime)}/</Typography>
                <Typography>{toHHMMSS(props.duration)}</Typography>
            </Box>
        </Stack>
        
       
    </Stack>
}

export function useDebounce(value, delay) {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = useState(value);
    React.useEffect(
        () => {
            // Update debounced value after delay
            let handler = debounce(() => {
                setDebouncedValue(value);
            }, delay, { 'maxWait': 30 });
            // Cancel the timeout if value changes (also on delay change or unmount)
            // This is how we prevent debounced value from updating if value is changed ...
            // .. within the delay period. Timeout gets cleared and restarted.
            return handler.cancel
        },
        [value, delay] // Only re-call effect if value or delay changes
    );
    return debouncedValue;
}
